import React, { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import timerService from '../../services/timerService';

// ──────────────────────────────────────────────
// Tiny helpers
// ──────────────────────────────────────────────
const glassCard = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '16px',
  backdropFilter: 'blur(12px)',
};

const pill = (active) => ({
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: '20px',
  px: 1.2,
  py: 0.25,
  fontSize: '0.65rem',
  fontWeight: 600,
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'all 0.2s',
  background: active ? 'rgba(229,57,53,0.18)' : 'rgba(255,255,255,0.06)',
  border: `1px solid ${active ? 'rgba(229,57,53,0.45)' : 'rgba(255,255,255,0.1)'}`,
  color: active ? '#e57373' : 'rgba(255,255,255,0.45)',
  '&:hover': { background: 'rgba(229,57,53,0.12)', color: '#e57373' },
});

// ──────────────────────────────────────────────
// Task progress bar
// ──────────────────────────────────────────────
const ProgressBar = ({ completed, estimated }) => {
  const pct = estimated > 0 ? Math.min(100, (completed / estimated) * 100) : 0;
  return (
    <Box
      sx={{
        height: 3,
        borderRadius: 2,
        background: 'rgba(255,255,255,0.07)',
        mt: 0.6,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          height: '100%',
          width: `${pct}%`,
          background: 'linear-gradient(90deg, #e53935, #ff7043)',
          borderRadius: 2,
          transition: 'width 0.5s ease',
        }}
      />
    </Box>
  );
};

// ──────────────────────────────────────────────
// Single task row
// ──────────────────────────────────────────────
const TaskRow = ({ task, isActive, onActivate, onDeactivate, onMarkDone, onDelete, onUpdate }) => {
  const [hovered, setHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(task.name || '');
  const [editEst, setEditEst] = useState(task.estimatedPomodoros != null ? task.estimatedPomodoros : '');
  const [showTimerSettings, setShowTimerSettings] = useState(false);
  const [editFocus, setEditFocus] = useState(task.taskFocusDuration != null ? Math.round(task.taskFocusDuration / 60) : null);
  const [editShortBreak, setEditShortBreak] = useState(task.taskShortBreak != null ? Math.round(task.taskShortBreak / 60) : null);
  const [editLongBreak, setEditLongBreak] = useState(task.taskLongBreak != null ? Math.round(task.taskLongBreak / 60) : null);
  const [editLongBreakAfter, setEditLongBreakAfter] = useState(task.taskLongBreakAfter ?? null);
  const [saving, setSaving] = useState(false);

  const hasTaskTimerSettings = task.taskFocusDuration != null || task.taskShortBreak != null
    || task.taskLongBreak != null || task.taskLongBreakAfter != null;

  useEffect(() => {
    setEditName(task.name || '');
    setEditEst(task.estimatedPomodoros != null ? task.estimatedPomodoros : '');
    setEditFocus(task.taskFocusDuration != null ? Math.round(task.taskFocusDuration / 60) : null);
    setEditShortBreak(task.taskShortBreak != null ? Math.round(task.taskShortBreak / 60) : null);
    setEditLongBreak(task.taskLongBreak != null ? Math.round(task.taskLongBreak / 60) : null);
    setEditLongBreakAfter(task.taskLongBreakAfter ?? null);
  }, [task.name, task.estimatedPomodoros, task.taskFocusDuration, task.taskShortBreak, task.taskLongBreak, task.taskLongBreakAfter]);

  const hasProg = task.estimatedPomodoros != null && task.estimatedPomodoros > 0;

  const handleSaveEdit = async (e) => {
    if (e) e.stopPropagation();
    const trimmed = editName.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const estNum = parseInt(editEst, 10);
      const dto = {
        name: trimmed,
        estimatedPomodoros: !isNaN(estNum) && estNum > 0 ? estNum : 0,
        // Per-task timer overrides: 0 means "clear the override", null means "don’t touch it"
        taskFocusDuration: editFocus != null ? editFocus * 60 : 0,
        taskShortBreak: editShortBreak != null ? editShortBreak * 60 : 0,
        taskLongBreak: editLongBreak != null ? editLongBreak * 60 : 0,
        taskLongBreakAfter: editLongBreakAfter != null ? editLongBreakAfter : 0,
      };
      if (onUpdate) {
        await onUpdate(task.id, dto);
      }
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = (e) => {
    if (e) e.stopPropagation();
    setEditName(task.name || '');
    setEditEst(task.estimatedPomodoros != null ? task.estimatedPomodoros : '');
    setEditFocus(task.taskFocusDuration != null ? Math.round(task.taskFocusDuration / 60) : null);
    setEditShortBreak(task.taskShortBreak != null ? Math.round(task.taskShortBreak / 60) : null);
    setEditLongBreak(task.taskLongBreak != null ? Math.round(task.taskLongBreak / 60) : null);
    setEditLongBreakAfter(task.taskLongBreakAfter ?? null);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          px: 1.5,
          py: 1.2,
          borderRadius: '10px',
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(229,57,53,0.35)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          my: 0.25,
        }}
      >
        <Box
          component="input"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSaveEdit();
            if (e.key === 'Escape') handleCancelEdit();
          }}
          placeholder="Task name"
          autoFocus
          sx={{
            width: '100%',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '6px',
            color: 'white',
            fontSize: '0.82rem',
            px: 1.2,
            py: 0.6,
            outline: 'none',
            '&:focus': { borderColor: 'rgba(229,57,53,0.5)', boxShadow: '0 0 0 2px rgba(229,57,53,0.12)' },
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>
              Est. 🍅
            </Typography>
            <Box
              component="input"
              type="number"
              min="0"
              max="99"
              placeholder="0 (none)"
              value={editEst}
              onChange={(e) => setEditEst(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveEdit();
                if (e.key === 'Escape') handleCancelEdit();
              }}
              sx={{
                width: 75,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '6px',
                color: 'white',
                fontSize: '0.78rem',
                px: 1,
                py: 0.4,
                outline: 'none',
                '&::placeholder': { color: 'rgba(255,255,255,0.25)' },
                '&:focus': { borderColor: 'rgba(229,57,53,0.5)' },
              }}
            />
          </Box>

          {/* Timer settings toggle */}
          <Box
            onClick={() => setShowTimerSettings((v) => !v)}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.4,
              px: 0.9, py: 0.3, borderRadius: '8px', cursor: 'pointer', fontSize: '0.7rem',
              background: showTimerSettings ? 'rgba(229,57,53,0.15)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${showTimerSettings ? 'rgba(229,57,53,0.35)' : 'rgba(255,255,255,0.1)'}`,
              color: showTimerSettings ? '#e57373' : 'rgba(255,255,255,0.45)',
              transition: 'all 0.2s',
              '&:hover': { background: 'rgba(229,57,53,0.12)', color: '#e57373' },
            }}
          >
            ⏱ Timer
          </Box>
        </Box>

        {/* Per-task timer settings */}
        {showTimerSettings && (
          <Box
            sx={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '8px',
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>
              Timer overrides (leave at default to use global settings)
            </Typography>

            {/* Focus duration */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.75rem' }}>Focus</Typography>
                <Typography sx={{ color: editFocus != null ? '#e57373' : 'rgba(255,255,255,0.3)', fontSize: '0.75rem', fontWeight: 600 }}>
                  {editFocus != null ? `${editFocus} min` : 'default'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="input" type="range"
                  min={5} max={60} step={5}
                  value={editFocus ?? 25}
                  onChange={(e) => setEditFocus(Number(e.target.value))}
                  style={{ flex: 1, accentColor: '#e53935' }}
                />
                {editFocus != null && (
                  <Box onClick={() => setEditFocus(null)} sx={{ cursor: 'pointer', color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem', '&:hover': { color: '#ef4444' } }}>×</Box>
                )}
              </Box>
            </Box>

            {/* Short break */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.75rem' }}>Short Break</Typography>
                <Typography sx={{ color: editShortBreak != null ? '#4ade80' : 'rgba(255,255,255,0.3)', fontSize: '0.75rem', fontWeight: 600 }}>
                  {editShortBreak != null ? `${editShortBreak} min` : 'default'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="input" type="range"
                  min={1} max={15} step={1}
                  value={editShortBreak ?? 5}
                  onChange={(e) => setEditShortBreak(Number(e.target.value))}
                  style={{ flex: 1, accentColor: '#22c55e' }}
                />
                {editShortBreak != null && (
                  <Box onClick={() => setEditShortBreak(null)} sx={{ cursor: 'pointer', color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem', '&:hover': { color: '#ef4444' } }}>×</Box>
                )}
              </Box>
            </Box>

            {/* Long break */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.75rem' }}>Long Break</Typography>
                <Typography sx={{ color: editLongBreak != null ? '#60a5fa' : 'rgba(255,255,255,0.3)', fontSize: '0.75rem', fontWeight: 600 }}>
                  {editLongBreak != null ? `${editLongBreak} min` : 'default'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="input" type="range"
                  min={5} max={30} step={5}
                  value={editLongBreak ?? 15}
                  onChange={(e) => setEditLongBreak(Number(e.target.value))}
                  style={{ flex: 1, accentColor: '#3b82f6' }}
                />
                {editLongBreak != null && (
                  <Box onClick={() => setEditLongBreak(null)} sx={{ cursor: 'pointer', color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem', '&:hover': { color: '#ef4444' } }}>×</Box>
                )}
              </Box>
            </Box>

            {/* Long break after */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.75rem' }}>Long break after</Typography>
                <Typography sx={{ color: editLongBreakAfter != null ? '#f59e0b' : 'rgba(255,255,255,0.3)', fontSize: '0.75rem', fontWeight: 600 }}>
                  {editLongBreakAfter != null ? `${editLongBreakAfter} sessions` : 'default'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="input" type="range"
                  min={2} max={8} step={1}
                  value={editLongBreakAfter ?? 4}
                  onChange={(e) => setEditLongBreakAfter(Number(e.target.value))}
                  style={{ flex: 1, accentColor: '#f59e0b' }}
                />
                {editLongBreakAfter != null && (
                  <Box onClick={() => setEditLongBreakAfter(null)} sx={{ cursor: 'pointer', color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem', '&:hover': { color: '#ef4444' } }}>×</Box>
                )}
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 0.6 }}>
          <Box
            onClick={handleSaveEdit}
            title="Save changes"
            sx={{
              px: 1.3,
              py: 0.4,
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: 600,
              background: editName.trim() ? 'rgba(74,222,128,0.18)' : 'rgba(255,255,255,0.05)',
              color: editName.trim() ? '#4ade80' : 'rgba(255,255,255,0.2)',
              border: `1px solid ${editName.trim() ? 'rgba(74,222,128,0.4)' : 'rgba(255,255,255,0.1)'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              flex: 1,
              '&:hover': editName.trim() ? { background: 'rgba(74,222,128,0.28)' } : {},
            }}
          >
            {saving ? <CircularProgress size={12} sx={{ color: '#4ade80' }} /> : 'Save'}
          </Box>
          <Box
            onClick={handleCancelEdit}
            title="Cancel"
            sx={{
              px: 1.1,
              py: 0.4,
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.75rem',
              background: 'rgba(255,255,255,0.06)',
              color: 'rgba(255,255,255,0.6)',
              border: '1px solid rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              '&:hover': { background: 'rgba(255,255,255,0.14)', color: 'white' },
            }}
          >
            Cancel
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => (isActive ? onDeactivate() : onActivate(task.id))}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.2,
        px: 1.5,
        py: 1.1,
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        background: isActive
          ? 'rgba(229,57,53,0.10)'
          : hovered
          ? 'rgba(255,255,255,0.04)'
          : 'transparent',
        border: isActive
          ? '1px solid rgba(229,57,53,0.25)'
          : '1px solid transparent',
        boxShadow: isActive ? '0 0 12px rgba(229,57,53,0.12)' : 'none',
      }}
    >
      {/* Active indicator dot */}
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          mt: '5px',
          flexShrink: 0,
          background: isActive ? '#e53935' : 'rgba(255,255,255,0.15)',
          boxShadow: isActive ? '0 0 6px rgba(229,57,53,0.7)' : 'none',
          transition: 'all 0.3s',
        }}
      />

      {/* Task info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{
            color: isActive ? 'white' : 'rgba(255,255,255,0.75)',
            fontSize: '0.82rem',
            fontWeight: isActive ? 600 : 400,
            lineHeight: 1.3,
          }}
        >
          {task.name}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3 }}>
          <Box
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
            title="Click to edit expected pomodoros & task details"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 0.75,
              py: 0.15,
              borderRadius: '10px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)',
              color: 'rgba(255,255,255,0.45)',
              fontSize: '0.68rem',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                background: 'rgba(229,57,53,0.15)',
                borderColor: 'rgba(229,57,53,0.35)',
                color: '#e57373',
              },
            }}
          >
            <span>🍅 {task.completedPomodoros}{hasProg ? ` / ${task.estimatedPomodoros}` : ''}</span>
            <span style={{ fontSize: '0.62rem', opacity: 0.7 }}>✏️</span>
          </Box>
          {isActive && (
            <Typography sx={{ color: '#e57373', fontSize: '0.65rem', fontWeight: 600 }}>
              ● active
            </Typography>
          )}
        </Box>

        {hasProg && (
          <ProgressBar
            completed={task.completedPomodoros}
            estimated={task.estimatedPomodoros}
          />
        )}
      </Box>

      {/* Action buttons — visible on hover or when active */}
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          opacity: hovered || isActive ? 1 : 0,
          transition: 'opacity 0.2s',
          flexShrink: 0,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mark done */}
        <Box
          title="Mark done"
          onClick={() => onMarkDone(task.id)}
          sx={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.8rem',
            color: 'rgba(255,255,255,0.3)',
            '&:hover': { background: 'rgba(74,222,128,0.12)', color: '#4ade80' },
          }}
        >
          ✓
        </Box>
        {/* Edit */}
        <Box
          title="Edit expected pomodoros & task"
          onClick={() => setIsEditing(true)}
          sx={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.75rem',
            color: 'rgba(255,255,255,0.3)',
            '&:hover': { background: 'rgba(255,255,255,0.12)', color: 'white' },
          }}
        >
          ✏️
        </Box>
        {/* Delete */}
        <Box
          title="Delete task"
          onClick={() => onDelete(task.id)}
          sx={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.75rem',
            color: 'rgba(255,255,255,0.3)',
            '&:hover': { background: 'rgba(239,68,68,0.12)', color: '#ef4444' },
          }}
        >
          ✕
        </Box>
      </Box>
    </Box>
  );
};

// ──────────────────────────────────────────────
// Add-task form
// ──────────────────────────────────────────────
const AddTaskForm = ({ onAdd }) => {
  const [name, setName] = useState('');
  const [est, setEst] = useState('');
  const [showEst, setShowEst] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const dto = { name: trimmed };
      const estNum = parseInt(est, 10);
      if (!isNaN(estNum) && estNum > 0) dto.estimatedPomodoros = estNum;
      await onAdd(dto);
      setName('');
      setEst('');
      setShowEst(false);
      inputRef.current?.focus();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        borderTop: '1px solid rgba(255,255,255,0.06)',
        pt: 1.2,
      }}
    >
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
        <Box
          component="input"
          ref={inputRef}
          placeholder="Add a task…"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          sx={{
            flex: 1,
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: '8px',
            color: 'white',
            fontSize: '0.8rem',
            px: 1.2,
            py: 0.7,
            outline: 'none',
            '&::placeholder': { color: 'rgba(255,255,255,0.25)' },
            '&:focus': { borderColor: 'rgba(229,57,53,0.45)', boxShadow: '0 0 0 2px rgba(229,57,53,0.12)' },
          }}
        />

        {/* Toggle est. pomodoros field */}
        <Box
          onClick={() => setShowEst((v) => !v)}
          title="Set estimated pomodoros"
          sx={{
            width: 28,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.85rem',
            background: showEst ? 'rgba(229,57,53,0.12)' : 'rgba(255,255,255,0.05)',
            border: `1px solid ${showEst ? 'rgba(229,57,53,0.3)' : 'rgba(255,255,255,0.09)'}`,
            '&:hover': { background: 'rgba(229,57,53,0.12)' },
          }}
        >
          🍅
        </Box>

        {/* Submit */}
        <Box
          onClick={handleSubmit}
          sx={{
            width: 28,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem',
            background: name.trim()
              ? 'rgba(229,57,53,0.18)'
              : 'rgba(255,255,255,0.05)',
            border: `1px solid ${name.trim() ? 'rgba(229,57,53,0.4)' : 'rgba(255,255,255,0.09)'}`,
            color: name.trim() ? '#e57373' : 'rgba(255,255,255,0.2)',
            '&:hover': name.trim() ? { background: 'rgba(229,57,53,0.28)' } : {},
          }}
        >
          {saving ? <CircularProgress size={12} sx={{ color: '#e57373' }} /> : '+'}
        </Box>
      </Box>

      {/* Estimated pomodoros input */}
      <Collapse in={showEst}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="input"
            type="number"
            min="1"
            max="99"
            placeholder="Est. 🍅"
            value={est}
            onChange={(e) => setEst(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            sx={{
              width: 90,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: '8px',
              color: 'white',
              fontSize: '0.78rem',
              px: 1.2,
              py: 0.6,
              outline: 'none',
              '&::placeholder': { color: 'rgba(255,255,255,0.22)' },
              '&:focus': { borderColor: 'rgba(229,57,53,0.45)' },
            }}
          />
          <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.68rem' }}>
            estimated pomodoros
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

// ──────────────────────────────────────────────
// Main TaskPanel
// ──────────────────────────────────────────────
/**
 * Props:
 *   activeTaskId    — from timerState
 *   onStateUpdate   — called with new TimerStateDTO when activate/deactivate changes session
 */
const TaskPanel = ({ activeTaskId, onStateUpdate, refreshKey }) => {
  const [open, setOpen] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [showDone, setShowDone] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadTasks = async () => {
    try {
      const data = await timerService.getTasks();
      setTasks(data || []);
    } catch (e) {
      console.error('Failed to load tasks', e);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => { loadTasks(); }, []); // eslint-disable-line

  // Re-fetch whenever a pomodoro is completed (refreshKey increments)
  useEffect(() => {
    if (refreshKey > 0) {
      loadTasks();
    }
  }, [refreshKey]); // eslint-disable-line

  const handleAdd = async (dto) => {
    const task = await timerService.createTask(dto);
    setTasks((prev) => [task, ...prev]);
  };

  const handleActivate = async (id) => {
    try {
      const newState = await timerService.activateTask(id);
      onStateUpdate(newState);
    } catch (e) { console.error(e); }
  };

  const handleDeactivate = async () => {
    try {
      const newState = await timerService.deactivateTask();
      onStateUpdate(newState);
    } catch (e) { console.error(e); }
  };

  const handleMarkDone = async (id) => {
    try {
      const updated = await timerService.updateTask(id, { status: 'DONE' });
      setTasks((prev) => prev.map((t) => (t.id === id ? updated : t)));
      // If it was the active task, clear it in state
      if (id === activeTaskId) {
        const newState = await timerService.deactivateTask();
        onStateUpdate(newState);
      }
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    try {
      await timerService.deleteTask(id);
      setTasks((prev) => prev.filter((t) => t.id !== id));
      if (id === activeTaskId) {
        const newState = await timerService.deactivateTask();
        onStateUpdate(newState);
      }
    } catch (e) { console.error(e); }
  };

  const handleUpdate = async (id, dto) => {
    try {
      const updated = await timerService.updateTask(id, dto);
      setTasks((prev) => prev.map((t) => (t.id === id ? updated : t)));
      if (id === activeTaskId && dto.name && onStateUpdate) {
        timerService.getState().then((st) => {
          if (st) onStateUpdate(st);
        }).catch(() => {});
      }
    } catch (e) {
      console.error('Failed to update task', e);
    }
  };

  const activeTasks = tasks.filter((t) => t.status === 'ACTIVE');
  const doneTasks = tasks.filter((t) => t.status === 'DONE');

  return (
    <Box sx={{ width: '100%', maxWidth: 360, mx: 'auto', mt: 1 }}>
      <Box sx={{ ...glassCard, overflow: 'hidden' }}>
        {/* Header */}
        <Box
          onClick={() => setOpen((v) => !v)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.4,
            cursor: 'pointer',
            userSelect: 'none',
            '&:hover': { background: 'rgba(255,255,255,0.03)' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 1 }}
            >
              Tasks
            </Typography>
            {activeTasks.length > 0 && (
              <Box
                sx={{
                  background: 'rgba(229,57,53,0.18)',
                  border: '1px solid rgba(229,57,53,0.3)',
                  borderRadius: '10px',
                  px: 0.8,
                  py: 0.1,
                  fontSize: '0.62rem',
                  color: '#e57373',
                  fontWeight: 700,
                  lineHeight: 1.6,
                }}
              >
                {activeTasks.length}
              </Box>
            )}
          </Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.75rem', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }}>
            ▾
          </Typography>
        </Box>

        {/* Body */}
        <Collapse in={open}>
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} sx={{ color: 'rgba(229,57,53,0.6)' }} />
              </Box>
            ) : activeTasks.length === 0 ? (
              <Typography
                sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem', textAlign: 'center', py: 1.5 }}
              >
                No tasks yet — add one below
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {activeTasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    isActive={task.id === activeTaskId}
                    onActivate={handleActivate}
                    onDeactivate={handleDeactivate}
                    onMarkDone={handleMarkDone}
                    onDelete={handleDelete}
                    onUpdate={handleUpdate}
                  />
                ))}
              </Box>
            )}

            {/* Done tasks accordion */}
            {doneTasks.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Box
                  onClick={() => setShowDone((v) => !v)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    cursor: 'pointer',
                    py: 0.5,
                    px: 0.5,
                    borderRadius: '6px',
                    '&:hover': { background: 'rgba(255,255,255,0.03)' },
                  }}
                >
                  <Typography
                    sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: 0.8 }}
                  >
                    Completed ({doneTasks.length})
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.65rem' }}>
                    {showDone ? '▾' : '▸'}
                  </Typography>
                </Box>
                <Collapse in={showDone}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.25 }}>
                    {doneTasks.map((task) => (
                      <Box
                        key={task.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 1.5,
                          py: 0.9,
                          opacity: 0.5,
                        }}
                      >
                        <Typography sx={{ color: '#4ade80', fontSize: '0.75rem' }}>✓</Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem', textDecoration: 'line-through', flex: 1 }}>
                          {task.name}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.68rem' }}>
                          🍅 {task.completedPomodoros}
                        </Typography>
                        <Box
                          onClick={() => handleDelete(task.id)}
                          title="Remove"
                          sx={{
                            cursor: 'pointer',
                            color: 'rgba(255,255,255,0.2)',
                            fontSize: '0.7rem',
                            '&:hover': { color: '#ef4444' },
                          }}
                        >
                          ✕
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            )}

            {/* Add task form */}
            <AddTaskForm onAdd={handleAdd} />
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default TaskPanel;
